import { useCallback, useEffect, useState } from 'react';
import { Button, Form, FormGroup } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useLangContext } from '../../providers/LangProvider/LangProviderContext.js';
import { getAppPartner } from '../Auth/authSelectors.js';
import { refreshApiKeyAction, userUpdateAction, userUpdatePasswordAction } from '../Partner/partnerActions.js';
import { SelectCountry } from '../../components/SelectCountry/SelectCountry.jsx';
import { getAvailableCountries } from '../../app/appSelectors.js';
import {
    getPartnerIsRefreshApiKeyLoadingSelector,
    getUserUpdateErrorSelector,
    getUserUpdatePasswordErrorSelector,
} from '../Partner/partnerSelectors.js';
import { BinIcon } from '../../components/Icon/Icon';
import { Tooltip } from '../../components/Tooltip/Tooltip.jsx';
import ProfileApiDocsModal, { useProfileApiDocsModal } from './ProfileApiDocsModal';
import {
    ProfileCredentialRowStyled,
    ProfileFormGridStyled,
    ProfilePageStyled,
    ProfileSectionStyled,
} from './styled/ProfileStyled';

function Profile() {
    const { getLangPath } = useLangContext();
    const dispatch = useDispatch();
    const partnerInfo = useSelector(getAppPartner);
    const availableCountries = useSelector(getAvailableCountries);
    const userUpdateError = useSelector(getUserUpdateErrorSelector);
    const userUpdatePasswordError = useSelector(getUserUpdatePasswordErrorSelector);
    const isRefreshApiKeyLoading = useSelector(getPartnerIsRefreshApiKeyLoadingSelector);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const apiDocsModal = useProfileApiDocsModal();

    const handleRefreshApiKeyButtonClick = useCallback(() => {
        const isConfirmed = window.confirm(getLangPath('profile.refresh_api_key_confirm'));
        if (isConfirmed) {
            dispatch(refreshApiKeyAction());
        }
    }, [getLangPath, dispatch]);

    const handleCopyApiKey = useCallback(() => {
        if (partnerInfo?.api_key) {
            navigator.clipboard.writeText(partnerInfo.api_key).then();
        }
    }, [partnerInfo?.api_key]);

    useEffect(() => {
        if (!selectedCountry && availableCountries.length) {
            const found = availableCountries.find((item) => item.iso === partnerInfo.country);
            if (found) {
                setSelectedCountry(found);
            }
        }
    }, [availableCountries, partnerInfo.country, selectedCountry]);

    const submitProfileHandler = useCallback(
        (e) => {
            e.preventDefault();
            dispatch(
                userUpdateAction({
                    full_name: e.target.querySelector('[name=full_name]').value,
                    email: e.target.querySelector('[name=email]').value,
                    phone: e.target.querySelector('[name=phone]').value,
                }),
            );
        },
        [dispatch],
    );

    const submitChangePasswordHandler = useCallback(
        (e) => {
            e.preventDefault();
            dispatch(
                userUpdatePasswordAction({
                    old_password: e.target.querySelector('[name=old_password]').value,
                    new_password: e.target.querySelector('[name=new_password]').value,
                    new_password_repeat: e.target.querySelector('[name=new_password_repeat]').value,
                }),
            );
        },
        [dispatch],
    );

    const handleSelectCountry = useCallback(
        (iso) => {
            const found = availableCountries.find((item) => item.iso === iso);
            if (found) {
                setSelectedCountry(found);
                dispatch(
                    userUpdateAction({
                        country: found.iso,
                    }),
                );
            }
        },
        [availableCountries, dispatch],
    );

    return (
        <ProfilePageStyled>
            <div className="page-header">
                <span className="section-eyebrow">{getLangPath('profile.eyebrow')}</span>
                <h1 className="page-title">{getLangPath('profile.title')}</h1>
                <p className="page-desc">{getLangPath('profile.desc')}</p>
            </div>

            <ProfileSectionStyled>
                <div className="section-header">
                    <div className="section-heading">
                        <span className="section-eyebrow">{getLangPath('profile.section_profile_step')}</span>
                        <h2 className="section-title">{getLangPath('profile.section_profile_title')}</h2>
                        <p className="section-desc">{getLangPath('profile.section_profile_desc')}</p>
                    </div>
                </div>

                <Form onSubmit={submitProfileHandler}>
                    <ProfileFormGridStyled>
                        <FormGroup>
                            <Form.Label>{getLangPath('profile.country')}</Form.Label>
                            <SelectCountry
                                availableCountries={availableCountries}
                                onSelect={handleSelectCountry}
                                selectedCountry={selectedCountry}
                                error={null}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Form.Label>{getLangPath('profile.full_name')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="full_name"
                                defaultValue={partnerInfo.full_name}
                                required
                            />
                        </FormGroup>
                        <FormGroup>
                            <Form.Label>{getLangPath('profile.email')}</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                defaultValue={partnerInfo.email}
                                required
                            />
                        </FormGroup>
                        <FormGroup>
                            <Form.Label>{getLangPath('profile.phone')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="phone"
                                defaultValue={partnerInfo.phone}
                                required
                            />
                        </FormGroup>
                    </ProfileFormGridStyled>

                    {!!userUpdateError && <p className="section-error">{getLangPath(userUpdateError)}</p>}

                    <div className="section-actions">
                        <Button variant="primary" type="submit">
                            {getLangPath('profile.save_profile')}
                        </Button>
                    </div>
                </Form>
            </ProfileSectionStyled>

            <ProfileSectionStyled>
                <div className="section-header">
                    <div className="section-heading">
                        <span className="section-eyebrow">{getLangPath('profile.section_password_step')}</span>
                        <h2 className="section-title">{getLangPath('profile.change_password_title')}</h2>
                        <p className="section-desc">{getLangPath('profile.section_password_desc')}</p>
                    </div>
                </div>

                <Form onSubmit={submitChangePasswordHandler}>
                    <ProfileFormGridStyled>
                        <FormGroup>
                            <Form.Label>{getLangPath('profile.old_password')}</Form.Label>
                            <Form.Control type="password" name="old_password" autoComplete="current-password" />
                        </FormGroup>
                        <FormGroup>
                            <Form.Label>{getLangPath('profile.new_password')}</Form.Label>
                            <Form.Control type="password" name="new_password" autoComplete="new-password" />
                        </FormGroup>
                        <FormGroup>
                            <Form.Label>{getLangPath('profile.new_password_repeat')}</Form.Label>
                            <Form.Control type="password" name="new_password_repeat" autoComplete="new-password" />
                        </FormGroup>
                    </ProfileFormGridStyled>

                    {!!userUpdatePasswordError && (
                        <p className="section-error">{getLangPath(userUpdatePasswordError)}</p>
                    )}

                    <div className="section-actions">
                        <Button variant="primary" type="submit">
                            {getLangPath('profile.save_change_password')}
                        </Button>
                    </div>
                </Form>
            </ProfileSectionStyled>

            <ProfileSectionStyled>
                <div className="section-header">
                    <div className="section-heading">
                        <span className="section-eyebrow">{getLangPath('profile.section_api_step')}</span>
                        <h2 className="section-title">{getLangPath('profile.section_api_title')}</h2>
                        <p className="section-desc">{getLangPath('profile.section_api_desc')}</p>
                    </div>
                </div>

                <ProfileCredentialRowStyled>
                    <span className="credential-label">{getLangPath('profile.partner_id')}</span>
                    <div className="credential-value">
                        <p className="credential-text">{partnerInfo.partner_uid}</p>
                    </div>
                </ProfileCredentialRowStyled>

                <ProfileCredentialRowStyled>
                    <span className="credential-label">{getLangPath('profile.api_key')}</span>
                    <div className="credential-value">
                        <p className="credential-text">{partnerInfo.api_key}</p>
                        <Tooltip text={getLangPath('profile.copy_api_key')}>
                            <Button variant="info" size="sm" onClick={handleCopyApiKey} type="button">
                                <BinIcon name="IcDocCopy" size="16px" />
                            </Button>
                        </Tooltip>
                    </div>
                </ProfileCredentialRowStyled>

                <div className="section-actions">
                    <Button variant="outline-info" className="btn-md" onClick={apiDocsModal.open} type="button">
                        {getLangPath('profile.api_docs.open')}
                    </Button>
                    <Button
                        disabled={isRefreshApiKeyLoading}
                        onClick={handleRefreshApiKeyButtonClick}
                        variant="outline-danger"
                        type="button"
                    >
                        {getLangPath('profile.refresh_api_key_button')}
                    </Button>
                </div>
            </ProfileSectionStyled>

            <ProfileApiDocsModal show={apiDocsModal.show} onHide={apiDocsModal.close} />
        </ProfilePageStyled>
    );
}

export default Profile;
